window.LocationMap = (function () {
  let map = null;
  let markers = [];
  let bars = [];
  let regions = [];
  let activeRegionHandle = null;
  let activeIndex = null;
  let sectionId = null;
  let userLat = null;
  let userLng = null;

  function getParam(name) {
    return new URLSearchParams(window.location.search).get(name);
  }

  function init() {
    sectionId = window.__locationMapSectionId;
    if (!sectionId) return;

    const barsEl = document.getElementById('map-bars-' + sectionId);
    const regionsEl = document.getElementById('map-regions-' + sectionId);
    bars = barsEl ? JSON.parse(barsEl.textContent) : [];
    regions = regionsEl ? JSON.parse(regionsEl.textContent) : [];

    const defaultRegion = (regions[0] && regions[0].handle) || 'zurich';
    activeRegionHandle = window.__initialRegion || getParam('region') || defaultRegion;

    highlightTab(activeRegionHandle);
    renderList(activeRegionHandle);
    initMap(activeRegionHandle);
    setupTabLinks();
    tryGeolocation();
  }

  function tryGeolocation() {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      function (pos) {
        userLat = pos.coords.latitude;
        userLng = pos.coords.longitude;
        if (map) {
          filterListByBounds();
        } else {
          renderList(activeRegionHandle);
        }
      },
      function () {}
    );
  }

  function formatDistance(km) {
    if (km < 1) return Math.round(km * 1000) + ' m';
    return (km < 10 ? km.toFixed(1) : Math.round(km)) + ' km';
  }

  function highlightTab(handle) {
    const tabs = document.querySelectorAll(
      '#map-region-tabs-' + sectionId + ' .map-region-tab'
    );
    tabs.forEach((tab) => {
      const active = tab.dataset.region === handle;
      tab.classList.toggle('is-active', active);
      tab.classList.toggle('opacity-50', !active);
      tab.classList.toggle('opacity-100', active);
      tab.classList.toggle('font-bold', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  function setupTabLinks() {
    const tabs = document.querySelectorAll(
      '#map-region-tabs-' + sectionId + ' .map-region-tab'
    );
    tabs.forEach((tab) => {
      if (!tab.getAttribute('href').startsWith('?')) return;
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        const handle = tab.dataset.region;
        history.replaceState(null, '', '?region=' + handle);
        activeRegionHandle = handle;
        activeIndex = null;
        highlightTab(handle);
        renderList(handle);
        updateMapView(handle);
      });
    });
  }

  const FALLBACK_CENTERS = {
    zurich:  { lat: 47.3769, lng: 8.5417, zoom: 12, radius: 20 },
    basel:   { lat: 47.5596, lng: 7.5886, zoom: 14, radius: 15 },
    eglisau: { lat: 47.5736, lng: 8.5222, zoom: 15, radius: 8  },
  };

  function haversine(lat1, lng1, lat2, lng2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function barsForRegion(handle) {
    const byField = bars.filter((b) => b.regionHandle === handle);
    if (byField.length > 0) return byField;

    // Fall back to geographic proximity when regionHandle isn't assigned yet
    const region = regions.find((r) => r.handle === handle);
    const fallbackRadius = (FALLBACK_CENTERS[handle] && FALLBACK_CENTERS[handle].radius) || 20;
    const center = (region && region.lat)
      ? { lat: parseFloat(region.lat), lng: parseFloat(region.lng), radius: fallbackRadius }
      : FALLBACK_CENTERS[handle];
    if (!center) return [];

    return bars.filter((b) => {
      if (b.lat === null || b.lng === null) return false;
      return haversine(center.lat, center.lng, b.lat, b.lng) <= center.radius;
    });
  }

  function renderList(regionHandle, displayBars) {
    const list = document.getElementById('map-list-' + sectionId);
    if (!list) return;

    const allRegionBars = barsForRegion(regionHandle);
    let barsToShow = displayBars || allRegionBars;
    if (activeIndex === null) barsToShow = orderByProximity(barsToShow);
    barsToShow = sortClosedLast(barsToShow);
    if (activeIndex !== null) {
      const activeBar = allRegionBars[activeIndex];
      if (activeBar && barsToShow.includes(activeBar)) {
        barsToShow = [activeBar, ...barsToShow.filter((b) => b !== activeBar)];
      }
    }

    if (allRegionBars.length === 0) {
      list.innerHTML =
        '<p class="p-6 text-sm opacity-50">No locations in this region yet.</p>';
      return;
    }

    if (barsToShow.length === 0) {
      list.innerHTML =
        '<p class="p-6 text-sm opacity-50">No locations in this area. Try zooming out.</p>';
      return;
    }

    list.innerHTML = barsToShow
      .map((bar) => {
        const originalIndex = allRegionBars.indexOf(bar);
        const isActive = activeIndex !== null && originalIndex === activeIndex;
        return `
      <a
        href="${escHtml(bar.url)}"
        class="location-card${isActive ? ' is-active' : ''} flex items-start gap-3 p-4 border-b border-scheme-border last:border-b-0 no-underline"
        data-bar-handle="${escHtml(bar.handle)}"
        data-index="${originalIndex}"
        role="listitem"
      >
        ${
          bar.imageUrl
            ? `<div class="w-24 shrink-0 rounded-lg overflow-hidden" style="aspect-ratio:4/3">
                <img src="${escHtml(bar.imageUrl)}" alt="${escHtml(bar.name)}" class="w-full h-full object-cover${bar.isClosed ? ' location-image--closed' : ''}" loading="lazy" width="96" height="72">
               </div>`
            : ''
        }
        <div class="min-w-0 flex-1">
          <span class="block font-heading text-base break-words">${escHtml(bar.name)}${bar.isClosed ? ` <span class="location-closed-suffix">(${escHtml(bar.closedLabel)})</span>` : ''}</span>
          ${
            bar.street
              ? `<p class="text-base mt-0.5 opacity-60 break-words">${escHtml(bar.street)}, ${(document.documentElement.lang.startsWith('de') || document.documentElement.lang.startsWith('fr')) && bar.postal ? escHtml(bar.postal) + ' ' + escHtml(bar.city) : escHtml(bar.city) + (bar.postal ? ' ' + escHtml(bar.postal) : '')}</p>`
              : ''
          }
          ${
            userLat !== null && bar.lat !== null && bar.lng !== null
              ? `<p class="text-sm mt-0.5 opacity-50">${formatDistance(haversine(userLat, userLng, parseFloat(bar.lat), parseFloat(bar.lng)))}</p>`
              : ''
          }
        </div>
      </a>
    `;
      })
      .join('');
  }

  function sortClosedLast(list) {
    return list.slice().sort((a, b) => (a.isClosed ? 1 : 0) - (b.isClosed ? 1 : 0));
  }

  function orderByProximity(list) {
    if (userLat !== null) {
      return list.slice().sort((a, b) => {
        const aHas = a.lat !== null && a.lng !== null;
        const bHas = b.lat !== null && b.lng !== null;
        if (!aHas || !bHas) return aHas ? -1 : bHas ? 1 : 0;
        return (
          haversine(userLat, userLng, parseFloat(a.lat), parseFloat(a.lng)) -
          haversine(userLat, userLng, parseFloat(b.lat), parseFloat(b.lng))
        );
      });
    }
    const fallbackOrder = window.__mapFallbackOrder;
    if (!fallbackOrder || !fallbackOrder.length) return list;
    const rest = list.filter((b) => !fallbackOrder.includes(b.handle));
    const ordered = fallbackOrder
      .map((handle) => list.find((b) => b.handle === handle))
      .filter(Boolean);
    return [...ordered, ...rest];
  }

  function regionCenter(handle) {
    const region = regions.find((r) => r.handle === handle);
    const isMobile = window.innerWidth < 990;

    if (region && region.lat) {
      const lat = (isMobile && region.mobileLat) ? parseFloat(region.mobileLat) : parseFloat(region.lat);
      const lng = (isMobile && region.mobileLng) ? parseFloat(region.mobileLng) : parseFloat(region.lng);
      const zoom = isMobile && region.mobileZoom
        ? parseInt(region.mobileZoom)
        : (region.zoom ? parseInt(region.zoom) : 13);
      return { lat, lng, zoom };
    }
    const fb = FALLBACK_CENTERS[handle];
    return fb ? { lat: fb.lat, lng: fb.lng, zoom: fb.zoom } : { lat: 47.3769, lng: 8.5417, zoom: 12 };
  }

  function initMap(regionHandle) {
    const canvasEl = document.getElementById('map-canvas-' + sectionId);
    if (!canvasEl || typeof google === 'undefined') return;

    const rc = regionCenter(regionHandle);
    const center = { lat: rc.lat, lng: rc.lng };
    const zoom = rc.zoom;

    map = new google.maps.Map(canvasEl, {
      center,
      zoom,
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP,
      },
      styles: mapStyles(),
    });

    placeMarkers(regionHandle);
    map.addListener('idle', filterListByBounds);
  }

  function filterListByBounds() {
    if (!map) return;
    if (window.innerWidth < 990) {
      renderList(activeRegionHandle);
      return;
    }
    const bounds = map.getBounds();
    if (!bounds) return;

    const allRegionBars = barsForRegion(activeRegionHandle);
    let visible = allRegionBars.filter(
      (b) =>
        b.lat !== null &&
        b.lng !== null &&
        bounds.contains(new google.maps.LatLng(parseFloat(b.lat), parseFloat(b.lng)))
    );

    if (activeIndex !== null) {
      const activeBar = allRegionBars[activeIndex];
      if (activeBar && visible.includes(activeBar)) {
        visible = [activeBar, ...visible.filter((b) => b !== activeBar)];
      }
    }

    renderList(activeRegionHandle, visible);
  }

  function placeMarkers(regionHandle) {
    markers.forEach((m) => m.setMap(null));
    markers = [];

    const filtered = barsForRegion(regionHandle).filter(
      (b) => b.lat !== null && b.lng !== null
    );

    filtered.forEach((bar, i) => {
      const position = { lat: parseFloat(bar.lat), lng: parseFloat(bar.lng) };
      const marker = new google.maps.Marker({
        position,
        map,
        title: bar.name,
        icon: markerIcon(false),
      });
      marker.addListener('click', () => activateLocation(i));
      markers.push(marker);
    });
  }

  function activateLocation(index) {
    if (!map) return;

    const filtered = barsForRegion(activeRegionHandle);
    const bar = filtered[index];
    if (!bar) return;

    activeIndex = index;
    markers.forEach((m, i) => m.setIcon(markerIcon(i === index)));

    if (bar.lat !== null && bar.lng !== null) {
      map.panTo({ lat: parseFloat(bar.lat), lng: parseFloat(bar.lng) });
    } else {
      // No position to pan to — re-render immediately
      filterListByBounds();
    }
  }

  function updateMapView(regionHandle) {
    if (!map) return;
    const rc = regionCenter(regionHandle);
    map.panTo({ lat: rc.lat, lng: rc.lng });
    map.setZoom(rc.zoom);
    placeMarkers(regionHandle);
    // idle event fires after pan/zoom settles and will update the list
  }

  function markerIcon(active) {
    return {
      url: active ? window.__mapPinActiveUrl : window.__mapPinUrl,
      scaledSize: new google.maps.Size(44, 50),
      anchor: new google.maps.Point(22, 22),
    };
  }

  function mapStyles() {
    return [
      // Base — white
      { featureType: 'all', elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
      { featureType: 'all', elementType: 'labels.text.fill', stylers: [{ color: '#333333' }] },
      { featureType: 'all', elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }, { weight: 2 }] },
      { featureType: 'all', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },

      // Water — grey
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
      { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },

      // Nature / parks — light grey
      { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#ebebeb' }] },
      { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e8e8e8' }] },
      { featureType: 'poi.park', elementType: 'labels', stylers: [{ visibility: 'off' }] },

      // Roads — light to mid grey
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
      { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#e0e0e0' }] },
      { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#f0f0f0' }] },
      { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
      { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
      { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#c9c9c9' }] },
      { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
      { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },

      // POI — all off except parks
      { featureType: 'poi', elementType: 'all', stylers: [{ visibility: 'off' }] },
      { featureType: 'poi.park', elementType: 'geometry', stylers: [{ visibility: 'on' }] },

      // Transit — off
      { featureType: 'transit', elementType: 'all', stylers: [{ visibility: 'off' }] },

      // Administrative
      { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#d6d6d6' }] },
      { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#262626' }] },
      { featureType: 'administrative.neighborhood', elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
    ];
  }

  function escHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  document.addEventListener('DOMContentLoaded', () => {
    if (typeof google !== 'undefined') init();
    if (getParam('region')) scrollToMap();
  });

  function scrollToMap() {
    sectionId = window.__locationMapSectionId;
    if (!sectionId) return;
    const section = document.getElementById('section-' + sectionId);
    if (section) {
      setTimeout(() => section.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }

  return { init };
})();
